import React, { useState } from "react";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSubTableContainer,
} from "@/common/styles";
import { BookingsSectionTitle } from "./BookingsDetails/styles";
import Table from "@/common/elements/Table";
import CustomModal from "./CustomModal";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import TextField from "@/common/elements/InputElements/TextField";
import Button from "@/common/elements/Button";
import SecureFormItem from "@/common/SecureFormItem";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getOptionsFromRefList } from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import notificationService from "@/util/notificationService";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const INSPECTIONS_HEADERS = [
  { key: "inspector",  text: "Inspector"   },
  { key: "actualDate", text: "Actual Date" },
  { key: "actualTime", text: "Actual Time" },
  { key: "problem",    text: "Problem"     },
  { key: "comments",   text: "Comments"    },
];

const ScheduledEventsInspections = ({ inspections = [], setInspections }) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));

  const [showModal, setShowModal]   = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form]                      = Form.useForm();

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: inspectionsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:WorkReleaseInspections", INSPECTIONS_HEADERS);

  const inspectorOptions = getOptionsFromRefList("InspectorList", true, false, true);

  const getInspectorName = (id) =>
    inspectorOptions.find((opt) => opt.value === id)?.label ?? id;

  const tableData = inspections.map((insp, idx) => {
    let dateStr = "", timeStr = "";
    if (insp.actualDate && insp.actualTime) {
      const dt = dayjs(`${insp.actualDate} ${insp.actualTime}`, "MM/DD/YYYY HH:mm");
      if (dt.isValid()) {
        dateStr = dt.format("MM/DD/YYYY");
        timeStr = dt.format("HH:mm");
      }
    }
    return {
      key:        String(idx),
      inspector:  getInspectorName(insp.inspectorStaffId) || "",
      actualDate: dateStr,
      actualTime: timeStr,
      problem:    insp.problem ? "True" : "False",
      comments:   insp.comments || "",
      _original:  insp,
    };
  });

  const handleAdd = () => {
    setSelectedRow(null);
    form.setFieldsValue({
      inspectorStaffId: userDetails?.staffId || null,
      actualDateTime:   dayjs(),
      problem:          false,
      comments:         "",
    });
    setShowModal(true);
  };

  const handleDetails = () => {
    if (!selectedRow) {
      notificationService.warning("No row selected.");
      return;
    }
    const rowData = selectedRow._original;
    let actualDateTimeVal = null;
    if (rowData.actualDate && rowData.actualTime) {
      const parsed = dayjs(`${rowData.actualDate} ${rowData.actualTime}`, "MM/DD/YYYY HH:mm");
      actualDateTimeVal = parsed.isValid() ? parsed : null;
    }
    form.setFieldsValue({
      inspectorStaffId: rowData.inspectorStaffId || userDetails?.staffId,
      actualDateTime:   actualDateTimeVal || dayjs(),
      problem:          !!rowData.problem,
      comments:         rowData.comments || "",
    });
    setShowModal(true);
  };

  const handleDelete = () => {
    if (!selectedRow) {
      notificationService.warning("No row selected.");
      return;
    }
    const idx = parseInt(selectedRow.key, 10);
    setInspections(inspections.filter((_, i) => i !== idx));
    setSelectedRow(null);
  };

  const handleSaveInspection = async () => {
    try {
      const values = await form.validateFields();
      const newInspection = {
        inspectorStaffId: values.inspectorStaffId || "",
        actualDate:       values.actualDateTime ? dayjs(values.actualDateTime).format("MM/DD/YYYY") : null,
        actualTime:       values.actualDateTime ? dayjs(values.actualDateTime).format("HH:mm")      : null,
        problem:          !!values.problem,
        comments:         values.comments || "",
      };

      if (selectedRow?._original) {
        const updated = [...inspections];
        updated[parseInt(selectedRow.key, 10)] = newInspection;
        setInspections(updated);
      } else {
        setInspections([...inspections, newInspection]);
      }

      setShowModal(false);
      setSelectedRow(null);
      form.resetFields();
    } catch (err) {
      console.error("Form validation error:", err);
    }
  };

  return (
    <>
      <BookingsSectionTitle>
        <span>Inspections</span>
      </BookingsSectionTitle>

      <StyledSubTableContainer>
        <StyledFlex justify="end" gap="10px" marginBottom="10px">
          <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={isConfiguring}>Add</Button>
          <Button disabled={!selectedRow || isConfiguring} onClick={handleDetails}>Details</Button>
          <Button disabled={!selectedRow || isConfiguring} onClick={handleDelete}>Delete</Button>

          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button htmlType="button" onClick={startConfiguring}>
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Do you want to save the current list view configuration?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button htmlType="button">{configButtonLabel}</Button>
            </Popconfirm>
          )}
        </StyledFlex>

        <Table
          ref={dataTableRef}
          selected={!selectedRow}
          headers={inspectionsHeaders}
          draggable={isConfiguring}
          data={tableData}
          onRowClick={(row) => setSelectedRow(row)}
        />
      </StyledSubTableContainer>

      {showModal && (
        <CustomModal
          show={showModal}
          header="Inspection Details"
          onHide={() => setShowModal(false)}
          size="lg"
          onSaveClick={handleSaveInspection}
          backdrop={false}
        >
          <Form form={form} name="ScheduledEventsInspectionsForm" layout="vertical">
            <StyledGridRow gutter={16}>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="inspectorStaffId"
                  formItemProps={{ name: "inspectorStaffId", label: "Inspector" }}
                >
                  <Select options={inspectorOptions} placeholder="Select Inspector" />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="actualDateTime"
                  formItemProps={{ name: "actualDateTime", label: "Actual Date/Time" }}
                >
                  <CommonDatePicker
                    format="MM/DD/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                  />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="problem"
                  formItemProps={{
                    name:          "problem",
                    label:         "Problem?",
                    layout:        "horizontal",
                    labelPostion:  "label-right",
                    paddingTop:    "24px",
                    valuePropName: "checked",
                  }}
                >
                  <Checkbox />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={24}>
                <SecureFormItem
                  fieldName="comments"
                  formItemProps={{ name: "comments", label: "Comments" }}
                >
                  <TextField inputType="textarea" rows="4" />
                </SecureFormItem>
              </StyledGridCol>

            </StyledGridRow>
          </Form>
        </CustomModal>
      )}
    </>
  );
};

export default ScheduledEventsInspections;